/**
 * Chapter030.js — Daily Sign In / Sign Out (OHSE 030)
 * Migrated from SWMS Platform Chapter030.tsx → JavaScript
 *
 * Simplified version without webcam capture — uses text input for signature
 * and URL input for photo.
 *
 * Props:
 *   data       {array}   signin_entries from swmsData
 *   onChange   {fn}      (newEntries) => void
 *   companyName {string} from companyInfo.name
 */

import { useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const inputCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';

function emptyEntry() {
  return {
    id: crypto.randomUUID(),
    date: new Date().toISOString().split('T')[0],
    name: '',
    signature: '',
    time_in: '',
    time_out: '',
    photo: '',
  };
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

export default function Chapter030({ data = [], onChange, companyName }) {
  const cn = companyName || '[Company Name]';

  const addEntry = useCallback(() => onChange([...data, emptyEntry()]), [data, onChange]);
  const removeEntry = useCallback((id) => onChange(data.filter(e => e.id !== id)), [data, onChange]);
  const updateEntry = useCallback((id, field, value) => {
    onChange(data.map(e => e.id === id ? { ...e, [field]: value } : e));
  }, [data, onChange]);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 030</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">Daily Sign In / Sign Out</h2>
      </div>

      {/* Declaration */}
      <div className="p-4 bg-[#f8f7f4] rounded-lg border border-[#e0dbd0] text-sm text-[#1a1a2e] leading-relaxed">
        <span className="font-semibold text-[#c9a84c]">{cn}</span>{' '}
        records all daily sign in and sign out entries in the following register.
      </div>

      {/* Entry Cards */}
      {data.length === 0 && (
        <div className="flex flex-col items-center justify-center py-10 text-center border border-[#e0dbd0] rounded-lg bg-white">
          <p className="text-sm text-gray-400 mb-3">No sign in/out records yet.</p>
          <button type="button" onClick={addEntry}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-[#1a1a2e] rounded hover:bg-[#252547] transition-colors">
            <Plus className="w-4 h-4" /> Add Entry
          </button>
        </div>
      )}

      {data.map((entry) => (
        <div key={entry.id} className="bg-white border border-[#e0dbd0] rounded-lg p-4 space-y-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-[#1a1a2e]">
              {entry.name || 'New Entry'} — {entry.date || 'No Date'}
            </span>
            <button type="button" onClick={() => removeEntry(entry.id)}
              className="p-1 text-red-400 hover:text-red-600 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {/* Date + Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Date">
              <input type="date" value={entry.date}
                onChange={e => updateEntry(entry.id, 'date', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Name">
              <input type="text" value={entry.name} placeholder="Full name"
                onChange={e => updateEntry(entry.id, 'name', e.target.value)} className={inputCls} />
            </Field>
          </div>

          {/* Signature */}
          <Field label="Signature (type name)">
            <input type="text" value={entry.signature} placeholder="Signature"
              onChange={e => updateEntry(entry.id, 'signature', e.target.value)} className={inputCls} />
          </Field>

          {/* Time In + Time Out */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Time In">
              <input type="time" value={entry.time_in}
                onChange={e => updateEntry(entry.id, 'time_in', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Time Out">
              <input type="time" value={entry.time_out}
                onChange={e => updateEntry(entry.id, 'time_out', e.target.value)} className={inputCls} />
            </Field>
          </div>

          {/* Photo URL */}
          <Field label="Photo URL">
            <input type="text" value={entry.photo} placeholder="Photo URL or data URL"
              onChange={e => updateEntry(entry.id, 'photo', e.target.value)} className={inputCls} />
          </Field>

          {entry.photo && (
            <div>
              <img src={entry.photo} alt="Sign in photo"
                className="max-h-24 border border-[#e0dbd0] rounded p-1 object-cover"
                onError={e => { e.target.style.display = 'none'; }} />
            </div>
          )}
        </div>
      ))}

      {data.length > 0 && (
        <div className="flex justify-center">
          <button type="button" onClick={addEntry}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1a1a2e] rounded hover:bg-[rgba(201,168,76,0.15)] border border-[#c9a84c]/40 transition-colors">
            <Plus className="w-4 h-4" /> Add Entry
          </button>
        </div>
      )}
    </div>
  );
}
